import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, AfterViewInit, effect, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { TripTypeTabs } from '../trip-type-tabs/trip-type-tabs.component';
import { TripSearchService, SearchRequest } from '../../core/services/trip-search.service';
import { DateUtilityService } from '../../core/services/date-utility.service';
import { CustomDateAdapter, CUSTOM_DATE_FORMATS } from '../../core/services/custom-date-adapter';
import { SnackbarService } from '../../core/services/snackbar.service';
import { BaseService } from '../../core/services/base.service';
import { ClientDetailsService } from '../../core/services/client-details.service';
import { City as CityModel } from '../../core/interfaces/city.interface';
import { PackageModel, VehicleModel } from '../../core/interfaces/vehicle-model.interface';
import { ClientDetails } from '../../models/client-details.model';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import { de } from 'date-fns/locale';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-trip-search-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    NgxMaterialTimepickerModule,
    TripTypeTabs
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
  ],
  templateUrl: './trip-search-form.component.html',
  styleUrls: ['./trip-search-form.component.scss']
})
export class TripSearchForm implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() pageType: string = '';
  @Input() selectedTripType: 'local' | 'outstation' | 'airport' = 'local';
  @Input() initialFormData: any = null;
  @Input() data: any = null;

  @Output() tripTypeChange = new EventEmitter<'local' | 'outstation' | 'airport'>();

  tripSearchForm: FormGroup;

  // Client details
  clientDetails: ClientDetails;
  clientId: number | null = null;

  // API data
  cities: CityModel[] = [];
  packages: PackageModel[] = [];
  vehicleTypes: VehicleModel[] = [];
  vehicles: VehicleModel[] = [];

  // Loading state
  isLoading = false;
  vehicleTypesLoaded = false;
  noVehicleTypesMessage = '';
  IsValidateReportingTime = false;
  noPackagesMessage = '';

  // State tracking
  private isInitialized = false;
  private isClientLoaded = false;
  private isCitiesLoaded = false;
  private pendingFormData: any = null;
  private pendingTripType: 'local' | 'outstation' | 'airport' | null = null;

  // Breakpoint observer for responsive design
  isMobile = false;
  isTablet = false;
  isDesktop = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private baseService: BaseService,
    private tripSearchService: TripSearchService,
    private dateUtilityService: DateUtilityService,
    private snackbarService: SnackbarService,
    private clientDetailsService: ClientDetailsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
    private ngZone: NgZone,
    private toastService: ToastService,
  ) {
    this.clientDetails = new ClientDetails();
    this.tripSearchForm = this.createForm();

    // Initialize breakpoint observer
    this.initializeBreakpointObserver();

    effect(() => {
      const searchFormData = this.tripSearchService.searchFormData();
      if (searchFormData) {
        // Defer until cities/options exist — patchValue alone leaves mat-select empty
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              this.pendingFormData = searchFormData;
              this.processPendingData();
            });
          });
        });
      }
    });

    // Effect to update pickup time when client details are loaded
    effect(() => {
      const beforeBookingTime = this.clientDetailsService.beforeBookingTime();
      if (beforeBookingTime && this.tripSearchForm) {
        // Update pickup time if it's currently set to the old default
        const pickupTimeControl = this.tripSearchForm.get('pickupTime');
        if (pickupTimeControl && pickupTimeControl.value === '08:00') {
          const newDefaultTime = this.calculateDefaultPickupTime();
          pickupTimeControl.setValue(newDefaultTime);
        }
      }
    });
  }

  private initializeBreakpointObserver() {
    this.breakpointObserver
      .observe([
        Breakpoints.HandsetPortrait,
        Breakpoints.HandsetLandscape,
        Breakpoints.TabletPortrait,
        Breakpoints.TabletLandscape,
        Breakpoints.WebPortrait,
        Breakpoints.WebLandscape
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = this.breakpointObserver.isMatched(Breakpoints.HandsetPortrait) ||
          this.breakpointObserver.isMatched(Breakpoints.HandsetLandscape);
        this.isTablet = this.breakpointObserver.isMatched(Breakpoints.TabletPortrait) ||
          this.breakpointObserver.isMatched(Breakpoints.TabletLandscape);
        this.isDesktop = this.breakpointObserver.isMatched(Breakpoints.WebPortrait) ||
          this.breakpointObserver.isMatched(Breakpoints.WebLandscape);

        // Use NgZone and setTimeout to defer change detection to prevent ExpressionChangedAfterItHasBeenCheckedError
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              this.cdr.detectChanges();
            });
          });
        });
      });
  }

  ngOnInit(): void {
    this.isInitialized = true;

    // Initialize form
    this.createForm();

    // Set default dates and times
    this.setDefaultDatesAndTimes();

    // Handle initial form data if available
    if (this.initialFormData) {
      this.pendingFormData = this.initialFormData;
    }
    if (this.data) {
      this.pendingFormData = this.data;
    }

    // Do not run tab change (clears form) when restoring saved search from input
    if (this.selectedTripType && !this.pendingFormData) {
      this.pendingTripType = this.selectedTripType;
    }

    // Subscribe to startDate changes
    this.tripSearchForm.get('startDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(startDate => {
        if (startDate) {
          const endDateControl = this.tripSearchForm.get('endDate');
          if (endDateControl) {
            if (this.selectedTripType === 'local' || this.selectedTripType === 'airport') {
              // For local and airport trips, end date should be same as start date
              endDateControl.setValue(startDate, { emitEvent: false });
            } else if (this.selectedTripType === 'outstation') {
              // For outstation trips, only update end date if it's before start date
              const currentEndDate = endDateControl.value;
              if (!currentEndDate || new Date(currentEndDate) < new Date(startDate)) {
                endDateControl.setValue(startDate, { emitEvent: false });
              }
            }
          }
        }
      });


    // Start loading client data
    this.getClient();

    // Handle booking-search page specific initialization
    if (this.pageType === 'bookingSearch') {
      this.handleBookingSearchPageInitialization();
    }

    if (this.pendingFormData) {
      this.processPendingData();
    }

  }

  ngAfterViewInit(): void {
    // Trigger change detection after view is ready - use NgZone and setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      });
    });

    // Process any pending data
    this.processPendingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle initialFormData changes
    if (changes['initialFormData']) {
      const currentValue = changes['initialFormData'].currentValue;
      const previousValue = changes['initialFormData'].previousValue;

      if (currentValue && currentValue !== previousValue) {
        this.pendingFormData = currentValue;
        this.processPendingData();
      } else if (currentValue && currentValue === previousValue) {
        this.pendingFormData = currentValue;
        this.processPendingData();
      }
    }

    // Handle selectedTripType changes
    if (changes['selectedTripType']) {
      const currentValue = changes['selectedTripType'].currentValue;
      const previousValue = changes['selectedTripType'].previousValue;

      if (currentValue && currentValue !== previousValue) {
        this.pendingTripType = currentValue;
        this.processPendingData();
      }
    }

    // Handle pageType changes
    if (changes['pageType']) {
      const currentValue = changes['pageType'].currentValue;
      const previousValue = changes['pageType'].previousValue;

      if (currentValue && currentValue !== previousValue) {
        if (currentValue === 'bookingSearch') {
          this.handleBookingSearchPageInitialization();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Process any pending data that was received before the component was ready
   */
  private processPendingData(): void {
    if (!this.isInitialized) {
      return;
    }

    // Process pending trip type
    if (this.pendingTripType) {
      this.selectedTripType = this.pendingTripType;
      this.onTripTypeChange(this.pendingTripType);
      this.pendingTripType = null;
    }

    // Process pending form data only when client and cities are loaded
    if (this.pendingFormData && this.isClientLoaded && this.isCitiesLoaded) {
      this.populateFormWithData(this.pendingFormData);
      this.pendingFormData = null;
    }

    // Ensure default dates and times are set
    this.setDefaultDatesAndTimes();
  }

  /**
   * Handle initialization specific to booking-search page
   */
  private handleBookingSearchPageInitialization(): void {
    // Process any pending data immediately
    this.processPendingData();

    // Ensure client data is loaded
    if (!this.isClientLoaded) {
      this.getClient();
    }
  }

  private createForm(): FormGroup {
    // Get current date and default time (8:00 AM)
    const currentDate = this.dateUtilityService.getCurrentDate();
    const defaultTime = '08:00'; // 8:00 AM

    const form = this.fb.group({
      cityId: ['0', [Validators.required]],
      vehicleTypeId: ['0', [Validators.required]],
      selectPackageType: ['0', [Validators.required]],
      startDate: [currentDate, [Validators.required]],
      endDate: [currentDate],
      pickupTime: [defaultTime, [
        Validators.required,
        Validators.pattern(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
      ]]
    });
    return form;
  }

  /**
   * Set default dates and times for the form
   */
  private setDefaultDatesAndTimes(): void {
    if (!this.tripSearchForm) {
      return;
    }

    // Set default dates using the date utility service
    this.dateUtilityService.setDefaultDates(this.tripSearchForm);

    // Set default pickup time if not already set
    const pickupTimeControl = this.tripSearchForm.get('pickupTime');
    if (pickupTimeControl && !pickupTimeControl.value) {
      const defaultTime = this.calculateDefaultPickupTime();
      pickupTimeControl.setValue(defaultTime);
    }
  }

  /**
   * Calculate default pickup time based on beforeBookingTime from API
   * Current date + 24 hours + beforeBookingTime + current time
   */
  private calculateDefaultPickupTime(): string {
    try {
      // Get beforeBookingTime from ClientDetailsService (default to '01:00' if not available)
      const beforeBookingTime = this.clientDetailsService.beforeBookingTime() || '';

      // Validate beforeBookingTime format
      if (!beforeBookingTime.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
        console.warn('Invalid beforeBookingTime format:', beforeBookingTime, 'using default 01:00');
        return '01:00';
      }

      // Get current date and time
      const currentDate = new Date();
      const tomorrow = new Date(currentDate);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Parse beforeBookingTime (format: 'HH:MM')
      const [beforeHours, beforeMinutes] = beforeBookingTime.split(':').map(Number);

      // Validate parsed values
      if (isNaN(beforeHours) || isNaN(beforeMinutes) || beforeHours < 0 || beforeHours > 23 || beforeMinutes < 0 || beforeMinutes > 59) {
        console.warn('Invalid time values from beforeBookingTime:', beforeBookingTime, 'using default 01:00');
        return '01:00';
      }

      // Get current time
      const currentHours = currentDate.getHours();
      const currentMinutes = currentDate.getMinutes();

      // Add beforeBookingTime to current time
      let totalHours = beforeHours + currentHours;
      let totalMinutes = beforeMinutes + currentMinutes;

      // Handle minute overflow
      if (totalMinutes >= 60) {
        totalHours += Math.floor(totalMinutes / 60);
        totalMinutes = totalMinutes % 60;
      }

      // Handle hour overflow (24-hour format)
      if (totalHours >= 24) {
        totalHours = totalHours % 24;
      }

      // Set the calculated time on tomorrow's date
      tomorrow.setHours(totalHours, totalMinutes, 0, 0);

      // Format as HH:MM
      const formattedTime = tomorrow.toTimeString().slice(0, 5);


      return formattedTime;
    } catch (error) {
      console.error('Error calculating default pickup time:', error);
      return '01:00'; // Fallback to default
    }
  }

  private getSearchParams(searchFor: 'PACKAGE' | 'VEHICLE' | 'PACKAGEDETAILS'): SearchRequest {
    const formValue = this.tripSearchForm.value;

    // Set default dates and times if not provided
    this.setDefaultDatesAndTimes();

    let activetab = this.selectedTripType == 'local' ? 1 : this.selectedTripType == 'outstation' ? 2 : 3;
    const vehicleTypeId = formValue.vehicleTypeId === '0' ? 0 : Number(formValue.vehicleTypeId) || 0;

    // Convert dates to proper format
    const startDate = formValue.startDate instanceof Date ? formValue.startDate : new Date(formValue.startDate);
    const endDate = formValue.endDate instanceof Date ? formValue.endDate : new Date(formValue.endDate);
    const pickupTime = this.tripSearchForm.get('pickupTime')?.value;

    return {
      clientId: this.clientId || 0,
      searchFor: searchFor,
      vehicleTypeId: vehicleTypeId,
      packageId: Number(formValue.selectPackageType) || 0,
      fromDate: this.dateUtilityService.formatDateForAPI(startDate),
      toDate: this.dateUtilityService.formatDateForAPI(endDate),
      cityId: formValue.cityId,
      packageTypeId: activetab,
      pickupTime: pickupTime
    };
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private handleError(error: any): void {
    const message = error?.error?.responseMessage || error?.message || 'An error occurred';
    this.snackbarService.openSnackBarError(message);
  }

  isFormValid(): boolean {
    if (!this.tripSearchForm) {
      return false;
    }
    // Use a more stable validation check to prevent change detection issues
    try {
      return this.tripSearchForm.valid;
    } catch (error) {
      return false;
    }
  }

  getFormErrors(): string[] {
    debugger;
    const errors: string[] = [];
    Object.keys(this.tripSearchForm.controls).forEach(key => {
      const control = this.tripSearchForm.get(key);
      if (control?.errors && control?.touched) {
        errors.push(`${key} is invalid`);
      }
    });
    return errors;
  }

  onSearch(): void {

    this.onPickupTimeChange();

    if (this.IsValidateReportingTime) {

      this.markFormGroupTouched();

      // Trigger comprehensive validation before submission
      this.validateForm();

      if (this.isFormValid() && !this.hasFormErrors()) {
        this.setLoading(true);
        debugger;
        // Get current form values
        const formValues = this.tripSearchForm.value;
        this.patchFormData(formValues);
        const pickupTime = this.tripSearchForm.get('pickupTime')?.value;

        const selectedCity = this.cities.find(city => city.cityId === formValues.cityId);
        const cityName = selectedCity ? selectedCity.cityName : '';

        const formDataWithTripType = {
          ...formValues,
          tripType: this.selectedTripType,
          cityName: cityName,
          clientId: this.clientId,
          clientName: this.clientDetails.clientName,
          pickupTime: pickupTime
        };    

        this.tripSearchService.setSearchFormData(formDataWithTripType);

        const apiUrl = '/api/B2COnlineBooking/SearchVehicleOrPackageNew';
        const searchParams = this.getSearchParams('PACKAGEDETAILS');

        this.baseService.postData(apiUrl, searchParams).subscribe({
          next: (response: any) => {

            debugger;

            if (response && response.data) {
              this.tripSearchService.setSearchVehicleData(response.data);
            }else{
              this.toastService.warning(response.responseMessage);
              this.setLoading(false);
              return;
            }

            // Only navigate if not already on booking-search page
            if (this.router.url !== '/booking-search') {
              this.router.navigate(['/booking-search']).then(() => {
                this.setLoading(false);
              }).catch((error) => {
                console.error('Navigation error:', error);
                this.setLoading(false);
                this.snackbarService.openSnackBarError('Navigation failed. Please try again.');
              });
            } else {
              // If already on booking-search page, trigger search and close mobile filter if needed
              this.setLoading(false);
              // Emit an event to notify the parent component that search is complete
              this.tripSearchService.setSearchFormData(formDataWithTripType);
            }
          },
          error: (error: any) => {
            console.error('API Error:', error);
            this.setLoading(false);
            this.snackbarService.openSnackBarError('Failed to search vehicles. Please try again.');
          }
        });
      }
    }
  }





  private markFormGroupTouched(): void {
    Object.keys(this.tripSearchForm.controls).forEach(key => {
      const control = this.tripSearchForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.tripSearchForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  getFieldError(fieldName: string): string {
    
    const field = this.tripSearchForm.get(fieldName);
    if (field && field.errors && (field.touched || field.dirty)) {
      const errors = field.errors;

      // Handle different types of validation errors
      if (errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }

      if (errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${errors['minlength'].requiredLength} characters`;
      }

      if (errors['maxlength']) {
        return `${this.getFieldLabel(fieldName)} must not exceed ${errors['maxlength'].requiredLength} characters`;
      }

      if (errors['pattern']) {
        return this.getPatternErrorMessage(fieldName, errors['pattern'].requiredPattern);
      }

      if (errors['email']) {
        return `${this.getFieldLabel(fieldName)} must be a valid email address`;
      }

      if (errors['min']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${errors['min'].min}`;
      }

      if (errors['max']) {
        return `${this.getFieldLabel(fieldName)} must not exceed ${errors['max'].max}`;
      }

      if (errors['dateInvalid']) {
        return `${this.getFieldLabel(fieldName)} must be a valid date`;
      }

      if (errors['dateRangeInvalid']) {
        return `${this.getFieldLabel(fieldName)} must be equal to or after the start date`;
      }

      if (errors['timeInvalid']) {
        return `${this.getFieldLabel(fieldName)} must be a valid time in 24-hour format`;
      }

      if (errors['cityNotAvailable']) {
        return `${this.getFieldLabel(fieldName)} is not available for this client`;
      }

      if (errors['packageNotAvailable']) {
        return `${this.getFieldLabel(fieldName)} is not available for the selected city`;
      }

      if (errors['vehicleNotAvailable']) {
        return `${this.getFieldLabel(fieldName)} is not available for the selected criteria`;
      }

      if (errors['dateInPast']) {
        return `${this.getFieldLabel(fieldName)} cannot be in the past`;
      }

      if (errors['dateTooFar']) {
        return `${this.getFieldLabel(fieldName)} cannot be more than 30 days in the future`;
      }

      if (errors['timeNotAvailable']) {
        return `${this.getFieldLabel(fieldName)} is not available for booking`;
      }

      // Handle custom API validation errors
      if (errors['apiError']) {
        return errors['apiError'];
      }

      // Default error message
      return `${this.getFieldLabel(fieldName)} is invalid`;
    }
    return '';
  }

  /**
   * Get pattern-specific error messages
   */
  private getPatternErrorMessage(fieldName: string, pattern: string): string {
    const fieldLabel = this.getFieldLabel(fieldName);

    // Common patterns and their error messages
    const patternMessages: { [key: string]: string } = {
      '^[0-9]+$': `${fieldLabel} must contain only numbers`,
      '^[a-zA-Z]+$': `${fieldLabel} must contain only letters`,
      '^[a-zA-Z0-9]+$': `${fieldLabel} must contain only letters and numbers`,
      '^[0-9]{10}$': `${fieldLabel} must be exactly 10 digits`,
      '^[0-9]{6}$': `${fieldLabel} must be exactly 6 digits`,
      '^[0-9]{4}$': `${fieldLabel} must be exactly 4 digits`,
      '^[0-9]{2}:[0-9]{2}$': `${fieldLabel} must be in HH:MM format`,
      '^[0-9]{1,2}:[0-9]{2}$': `${fieldLabel} must be in H:MM or HH:MM format`,
      '^[0-9]{1,2}:[0-9]{2}:[0-9]{2}$': `${fieldLabel} must be in HH:MM:SS format`,
      '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$': `${fieldLabel} must be a valid email address`,
      '^[0-9]{10,15}$': `${fieldLabel} must be a valid phone number (10-15 digits)`,
      '^[A-Z]{2}[0-9]{2}[A-Z]{2}[0-9]{4}$': `${fieldLabel} must be a valid vehicle number plate`,
      '^[0-9]{1,3}$': `${fieldLabel} must be a number between 1 and 999`,
      '^[0-9]+(\\.[0-9]{1,2})?$': `${fieldLabel} must be a valid number (up to 2 decimal places)`,
      '^([01]?[0-9]|2[0-3]):[0-5][0-9]$': `${fieldLabel} must be a valid 24-hour time format`
    };

    return patternMessages[pattern] || `${fieldLabel} format is invalid`;
  }

  /**
   * Set API validation error for a field
   */
  public setApiValidationError(fieldName: string, errorMessage: string): void {
    const field = this.tripSearchForm.get(fieldName);
    if (field) {
      field.setErrors({ apiError: errorMessage });
      field.markAsTouched();
      // Remove manual change detection to prevent ExpressionChangedAfterItHasBeenCheckedError
      // this.cdr.detectChanges();
    }
  }

  /**
   * Clear API validation error for a field
   */
  public clearApiValidationError(fieldName: string): void {
    const field = this.tripSearchForm.get(fieldName);
    if (field && field.errors && field.errors['apiError']) {
      const currentErrors = { ...field.errors };
      delete currentErrors['apiError'];

      if (Object.keys(currentErrors).length === 0) {
        field.setErrors(null);
      } else {
        field.setErrors(currentErrors);
      }
      // Remove manual change detection to prevent ExpressionChangedAfterItHasBeenCheckedError
      // this.cdr.detectChanges();
    }
  }

  /**
   * Validate date range for outstation trips
   */
  private validateDateRange(): void {
    const startDate = this.tripSearchForm.get('startDate')?.value;
    const endDate = this.tripSearchForm.get('endDate')?.value;

    if (startDate && endDate && this.selectedTripType === 'outstation') {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Set time to midnight for date comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end < start) {
        this.tripSearchForm.get('endDate')?.setErrors({ dateRangeInvalid: true });
      } else {
        this.clearApiValidationError('endDate');
      }
    }
  }

  /**
   * Validate pickup time
   */
  private validatePickupTime(): void {
    const pickupTime = this.tripSearchForm.get('pickupTime')?.value;

    if (pickupTime) {
      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

      if (!timePattern.test(pickupTime)) {
        this.tripSearchForm.get('pickupTime')?.setErrors({ timeInvalid: true });
      } else {
        this.clearApiValidationError('pickupTime');
      }
    }
  }

  /**
   * Validate start date is not in the past
   */
  private validateStartDate(): void {
    const startDate = this.tripSearchForm.get('startDate')?.value;

    if (startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(startDate);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        this.tripSearchForm.get('startDate')?.setErrors({ dateInPast: true });
      } else {
        this.clearApiValidationError('startDate');
      }

      // Check if date is too far in the future (30 days)
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 30);

      if (selectedDate > maxDate) {
        this.tripSearchForm.get('startDate')?.setErrors({ dateTooFar: true });
      }
    }
  }

  /**
   * Enhanced form validation
   */
  public validateForm(): void {
    this.validateStartDate();
    this.validateDateRange();
    this.validatePickupTime();

  }

  /**
   * Check if field has any validation errors (including API errors)
   */
  public hasFieldError(fieldName: string): boolean {
    const field = this.tripSearchForm.get(fieldName);
    return !!(field && field.errors && (field.touched || field.dirty));
  }

  /**
   * Get all validation errors for the form
   */
  public getAllFormErrors(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    Object.keys(this.tripSearchForm.controls).forEach(key => {
      const error = this.getFieldError(key);
      if (error) {
        errors[key] = error;
      }
    });

    return errors;
  }

  /**
   * Check if form has any validation errors
   */
  public hasFormErrors(): boolean {
    return Object.keys(this.getAllFormErrors()).length > 0;
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      cityId: 'City',
      selectPackageType: 'Package Type',
      vehicleTypeId: 'Vehicle Type',
      startDate: 'Start Date',
      endDate: 'End Date',
      pickupTime: 'Pickup Time'
    };
    return labels[fieldName] || fieldName;
  }

  get formControls() {
    return this.tripSearchForm.controls;
  }

  private extractUniqueVehicleTypes(vehicles: VehicleModel[]): VehicleModel[] {
    const uniqueTypes = new Set<number>();
    const uniqueVehicles: VehicleModel[] = [];

    vehicles.forEach(vehicle => {
      if (!uniqueTypes.has(vehicle.vehicleTypeId)) {
        uniqueTypes.add(vehicle.vehicleTypeId);
        uniqueVehicles.push(vehicle);
      }
    });

    return uniqueVehicles;
  }

  private mapApiResponseToVehicleModel(apiResponse: any[]): VehicleModel[] {
    const mappedVehicles: VehicleModel[] = [];
    apiResponse.forEach(item => {
      const vehicle: VehicleModel = {
        vehicleTypeId: item.vehicleTypeId || item.vehicleCategoryId || item.id || 0,
        vehicleTypeName: item.vehicleTypeName || item.vehicleCategory || item.name || '',
        vehicleTypeCode: item.vehicleTypeCode || item.vehicleCategory || item.code || '',
        isActive: item.isActive !== undefined ? item.isActive : true,
        capacity: item.capacity || 0,
        pricePerKm: item.pricePerKm || 0,
        imageUrl: item.imageUrl || '',
        description: item.description || ''
      };
      mappedVehicles.push(vehicle);
    });
    return mappedVehicles;
  }

  getClient(): void {
    this.tripSearchService.loadClientDetails().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (clientDetails: ClientDetails) => {
        this.clientDetails = clientDetails;
        this.clientId = this.clientDetails.clientId;
        this.isClientLoaded = true;

        // Load cities after client is loaded
        this.loadCities();

        // Process any pending data now that client is loaded
        this.processPendingData();
      },
      error: (error: any) => {
        this.handleError(error);
        this.isClientLoaded = true; // Mark as loaded even on error to prevent infinite retries
        this.snackbarService.openSnackBarError('Failed to load client details. Please try again.');
      }
    });
  }

  private loadCities(): void {
    if (!this.clientId) {
      this.snackbarService.openSnackBarError('Client ID is required to load cities.');
      return;
    }

    this.tripSearchService.loadCities(this.clientId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (cities: CityModel[]) => {
        this.cities = cities;
        this.isCitiesLoaded = true;

        // Process any pending data now that cities are loaded
        this.processPendingData();
      },
      error: (error: any) => {
        this.handleError(error);
        this.isCitiesLoaded = true; // Mark as loaded even on error to prevent infinite retries
        this.snackbarService.openSnackBarError('Failed to load cities. Please try again.');
      }
    });
  }

  private loadVehicleTypes(): void {
    if (!this.clientId) {
      this.snackbarService.openSnackBarError('Client ID is required to load vehicle types.');
      return;
    }

    const formValue = this.tripSearchForm.value;

    if (!formValue.startDate || !formValue.endDate) {
      this.dateUtilityService.setDefaultDates(this.tripSearchForm);
    }

    const searchParams = this.getSearchParams('VEHICLE');

    this.tripSearchService.loadVehicleTypes(searchParams).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (vehicleTypes: VehicleModel[]) => {
        this.vehicleTypes = this.mapApiResponseToVehicleModel(vehicleTypes);

        if (this.vehicleTypes.length > 0 && this.vehicleTypes[0].vehicleTypeId) {
          const uniqueVehicleTypeIds = new Set(this.vehicleTypes.map(v => v.vehicleTypeId));
          if (uniqueVehicleTypeIds.size < this.vehicleTypes.length) {
            this.vehicleTypes = this.extractUniqueVehicleTypes(this.vehicleTypes);
          }
        }

        const activeVehicleTypes = this.vehicleTypes.filter(v => v.isActive);
        this.vehicleTypesLoaded = true;

        if (activeVehicleTypes.length === 0) {
          this.noVehicleTypesMessage = 'No vehicle types available for the selected city and package.';
          this.setApiValidationError('vehicleTypeId', 'No vehicles found.');
        } else {
          this.noVehicleTypesMessage = '';
          this.clearApiValidationError('vehicleTypeId');
        }

        this.refreshVehicleTypeSelectAfterOptionsLoad();
        this.loadPackages();
      },
      error: (error: any) => {
        this.handleError(error);
        this.snackbarService.openSnackBarError('Failed to load vehicle types. Please try again.');
        this.vehicleTypesLoaded = true;
        this.noVehicleTypesMessage = 'Failed to load vehicle types.';

        const errorMessage = error?.error?.responseMessage || 'Failed to load vehicle types';
        this.setApiValidationError('vehicleTypeId', errorMessage);
      }
    });
  }

  onCityChange(): void {
    if (!this.clientId) {
      this.snackbarService.openSnackBarError('Client ID is required.');
      return;
    }

    const formValue = this.tripSearchForm.value;
    const cityId = formValue.cityId;

    if (!cityId) return;

    // Clear any API validation errors for city
    this.clearApiValidationError('cityId');

    // Set default dates and times if not provided
    this.setDefaultDatesAndTimes();
    this.loadPackages();
    this.loadVehicleTypes();

    // Trigger validation
    this.validateForm();
  }

  onPackageTypeChange(): void {
    this.loadVehicleTypes();
  }

  onVehicleTypeChange(): void {
    debugger;
    const selectedValue = this.tripSearchForm.get('selectPackageType')?.value;
    if(selectedValue === '0'){
      this.loadPackages();
    }
    
  }

  onPickupTimeChange(): void {
    debugger;
    const pickupTime = this.tripSearchForm.get('pickupTime')?.value;

    // if (pickupTime) {
    //   const startDate = this.tripSearchForm.get('startDate')?.value;
    //   const formattedStartDate = startDate instanceof Date ? startDate : new Date(startDate);

    //   const searchParams: any = {
    //     reportingTime: pickupTime,
    //     tripFromDate: this.dateUtilityService.formatDateForAPI(formattedStartDate),
    //   }
    //   this.tripSearchService.validateReportingTimeN(searchParams).pipe(takeUntil(this.destroy$)).subscribe({
    //     next: (isValid: string) => {
    //       debugger;
    //       this.IsValidateReportingTime = false;
    //       if (isValid === 'Success') {
    //         this.IsValidateReportingTime = true;
    //         this.setLoading(false);
    //       } else {
    //         this.IsValidateReportingTime = false;
    //         this.toastService.warning(isValid);
    //         this.setLoading(false);
    //       }
    //     }
    //   });
    // }
    this.IsValidateReportingTime = true
  }

  /**
   * mat-select matches option values with ===. Restored form data is often string (JSON/session);
   * API options use numbers. Refresh after options load so labels appear without extra navigation.
   */
  private refreshPackageSelectAfterOptionsLoad(): void {
    const control = this.tripSearchForm.get('selectPackageType');
    if (!control || this.packages.length === 0) {
      return;
    }
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '' || raw === '0') {
      return;
    }
    const match = this.packages.find(
      p => p.packageForId === Number(raw) || String(p.packageForId) === String(raw)
    );
    if (match) {
      control.setValue(match.packageForId, { emitEvent: false });
    }
    queueMicrotask(() => this.cdr.detectChanges());
  }

  private refreshVehicleTypeSelectAfterOptionsLoad(): void {
    const control = this.tripSearchForm.get('vehicleTypeId');
    if (!control || this.vehicleTypes.length === 0) {
      return;
    }
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '' || raw === '0') {
      return;
    }
    const match = this.vehicleTypes.find(
      v =>
        v.isActive &&
        (v.vehicleTypeId === Number(raw) || String(v.vehicleTypeId) === String(raw))
    );
    if (match) {
      control.setValue(match.vehicleTypeId, { emitEvent: false });
    }
    queueMicrotask(() => this.cdr.detectChanges());
  }

  private loadPackages(): void {
    if (!this.clientId) {
      this.snackbarService.openSnackBarError('Client ID is required to load packages.');
      return;
    }

    this.setLoading(true);

    if (!this.tripSearchForm.get('startDate')?.value || !this.tripSearchForm.get('endDate')?.value) {
      this.setDefaultDatesAndTimes();
    }

    const searchParams = this.getSearchParams('PACKAGE');

    this.tripSearchService.loadPackages(searchParams).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (packages: PackageModel[]) => {
        this.packages = packages;
        this.setLoading(false);

        if (this.packages.length === 0) {
          this.noPackagesMessage = 'No packages found.';
          this.setApiValidationError('selectPackageType', 'No packages found.');
        } else {
          this.noPackagesMessage = '';
          this.clearApiValidationError('selectPackageType');
        }
        this.refreshPackageSelectAfterOptionsLoad();
      },
      error: (error: any) => {
        this.handleError(error);
        this.snackbarService.openSnackBarError('Failed to load packages. Please try again.');
        this.setLoading(false);
      }
    });
  }

  private loadVehicles(): void {
    if (!this.clientId) {
      this.snackbarService.openSnackBarError('Client ID is required to load vehicles.');
      return;
    }

    this.setLoading(true);

    if (!this.tripSearchForm.get('startDate')?.value || !this.tripSearchForm.get('endDate')?.value) {
      this.setDefaultDatesAndTimes();
    }

    const searchParams = this.getSearchParams('VEHICLE');

    this.tripSearchService.loadVehicles(searchParams).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (vehicles: VehicleModel[]) => {
        this.vehicles = vehicles;
        this.setLoading(false);
      },
      error: (error: any) => {
        this.handleError(error);
        this.setLoading(false);
      }
    });
  }

  onTripTypeChange(tripType: 'local' | 'outstation' | 'airport'): void {
    // Always clear form fields when trip type changes manually
    this.clearFormFields();

    this.selectedTripType = tripType;
    this.tripTypeChange.emit(tripType);
  }

  /**
   * Public method to change trip type without clearing fields (for navigation)
   */
  public setTripTypeWithoutClearing(tripType: 'local' | 'outstation' | 'airport'): void {
    this.selectedTripType = tripType;
    this.tripTypeChange.emit(tripType);
  }

  /**
   * Clear form fields when trip type changes
   */
  private clearFormFields(): void {
    if (!this.tripSearchForm) {
      return;
    }

    // Get current date for default values
    const currentDate = this.dateUtilityService.getCurrentDate();
    const defaultTime = this.calculateDefaultPickupTime();

    // Clear form fields and set default values
    this.tripSearchForm.patchValue({
      cityId: '',
      selectPackageType: '0',
      vehicleTypeId: '0',
      startDate: currentDate,
      endDate: currentDate,
      pickupTime: defaultTime
    });

    // Clear validation errors
    this.tripSearchForm.markAsUntouched();
    this.tripSearchForm.markAsPristine();

    // Reset API data
    this.vehicleTypes = [];
    this.packages = [];
    this.vehicles = [];
    this.vehicleTypesLoaded = false;
    this.noVehicleTypesMessage = '';
  }

  /**
   * Populate form with initial data
   */
  private populateFormWithData(formData: any): void {
    if (!formData) {
      return;
    }

    // Ensure form is created
    if (!this.tripSearchForm) {
      this.tripSearchForm = this.createForm();
    }

    // Convert date strings to Date objects if they exist
    let startDate = null;
    let endDate = null;

    if (formData.startDate) {
      if (formData.startDate instanceof Date) {
        startDate = formData.startDate;
      } else {
        startDate = new Date(formData.startDate);
        if (isNaN(startDate.getTime())) {
          startDate = null;
        }
      }
    }

    if (formData.endDate) {
      if (formData.endDate instanceof Date) {
        endDate = formData.endDate;
      } else {
        endDate = new Date(formData.endDate);
        if (isNaN(endDate.getTime())) {
          endDate = null;
        }
      }
    }

    // Ensure start date is not after end date
    if (startDate && endDate && startDate > endDate) {
      // If start date is after end date, set end date to start date
      endDate = new Date(startDate);
    }

    // Ensure both dates are set to the same date for local trips
    if (this.selectedTripType === 'local' && startDate && endDate) {
      endDate = new Date(startDate);
    }

    // Prepare form values with proper defaults
    const formValues = {
      cityId: formData.cityId || '',
      selectPackageType: formData.selectPackageType || '',
      vehicleTypeId: formData.vehicleTypeId || '',
      startDate: startDate,
      endDate: endDate,
      pickupTime: formData.pickupTime || ''
    };

    // Set form values
    this.tripSearchForm.patchValue(formValues);

    // Set default dates and times if not provided
    this.setDefaultDatesAndTimes();

    // Set trip type if provided (without clearing fields during population)
    if (formData.tripType) {
      this.selectedTripType = formData.tripType;
      this.tripTypeChange.emit(formData.tripType);
    }

    // Trigger change detection - use NgZone and setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      });
    });

    this.markFormGroupTouched();
    this.triggerCityChangeFlow();
  }

  /**
   * Trigger the same flow as manual city selection
   */
  private triggerCityChangeFlow(): void {
    const formValue = this.tripSearchForm.value;

    if (this.clientId && formValue.cityId) {
      // Set default dates and times if not provided
      this.setDefaultDatesAndTimes();

      // Trigger the same API calls as onCityChange()
      this.loadPackages();
      this.loadVehicleTypes();
    } else {
      // Wait for client data to be loaded
      if (!this.isClientLoaded) {
        setTimeout(() => {
          this.triggerCityChangeFlow();
        }, 500);
      } else if (!this.isCitiesLoaded) {
        setTimeout(() => {
          this.triggerCityChangeFlow();
        }, 500);
      }
    }
  }

  /**
   * Validate if form has all required data for API calls
   */
  private validateFormDataForApiCalls(): boolean {
    const formValue = this.tripSearchForm.value;
    const hasClientId = !!this.clientId;
    const hasCityId = !!formValue.cityId;
    const hasCities = this.cities.length > 0;

    return hasClientId && hasCityId && hasCities;
  }

  /**
   * Public method to manually populate form with data
   */
  public populateForm(formData: any): void {
    // Ensure the form is created before populating
    if (!this.tripSearchForm) {
      this.tripSearchForm = this.createForm();
    }

    // If component is not ready, store as pending data
    if (!this.isInitialized || !this.isClientLoaded || !this.isCitiesLoaded) {
      this.pendingFormData = formData;
      return;
    }

    this.populateFormWithData(formData);

    // Trigger change detection after population - use NgZone and setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      });
    });
  }

  /**
   * Check if the form is ready to receive data
   */
  public isFormReady(): boolean {
    const ready = !!(this.tripSearchForm && this.isInitialized && this.isClientLoaded && this.isCitiesLoaded);
    return ready;
  }

  /**
   * Public method to check if all required data is loaded
   */
  public isDataLoaded(): boolean {
    const loaded = !!(this.isClientLoaded && this.isCitiesLoaded);
    return loaded;
  }

  /**
   * Public method to log current form state for debugging
   */
  public logFormState(): void {
    // Removed for production - only essential functionality remains
  }

  /**
   * Public method to force reload of vehicle types and packages
   */
  public reloadVehicleData(): void {
    this.triggerCityChangeFlow();
  }

  /**
   * Format date for display in dd-MMM-yyyy format
   */
  public formatDateForDisplay(date: Date | string | null): string {
    return this.dateUtilityService.formatDateForDisplay(date);
  }

  /**
   * Get formatted start date for display
   */
  public getFormattedStartDate(): string {
    const startDate = this.tripSearchForm.get('startDate')?.value;
    return this.formatDateForDisplay(startDate);
  }

  /**
   * Get formatted end date for display
   */
  public getFormattedEndDate(): string {
    const endDate = this.tripSearchForm.get('endDate')?.value;
    return this.formatDateForDisplay(endDate);
  }
  patchFormData
    (data: any) {
    if (data) {
      this.tripSearchForm.patchValue(data);
    }
  }
  patchVehicleData(data: any) {
    if (data) {
      this.tripSearchService.setSearchVehicleData(data);
    }
  }

}
